import { useCallback, useEffect, useState } from 'react'

interface User {
  id: string
  email: string
  name?: string
  balance: number
  status: 'active' | 'disabled'
  created_at: string
  last_login_at?: string
}

interface Transaction {
  id: string
  user_id: string
  points: number
  transaction_type: 'consume' | 'recharge'
  description?: string
  service_id?: string
  service_name?: string
  balance_after: number
  created_at: string
}

interface ServiceStat {
  service_id: string
  service_name: string
  total_calls: number
  successful_calls: number
  failed_calls: number
  total_points: number
  avg_response_time: number
  last_call_at: string
}

interface ApiKey {
  id: string
  user_id: string
  key_hash: string
  name: string
  note?: string
  status: 'active' | 'disabled'
  created_at: string
  last_used_at?: string
}

interface UserFilters {
  status: string
  balanceMin: string
  balanceMax: string
  dateFrom: string
  dateTo: string
  search: string
}

// API 调用辅助函数
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('admin_token')
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '请求失败')
  }

  return response.json()
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showAddPointsModal, setShowAddPointsModal] = useState(false)
  const [pointsToAdd, setPointsToAdd] = useState('')
  const [pointsNote, setPointsNote] = useState('')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loadingTransactions, setLoadingTransactions] = useState(false)
  const [serviceStats, setServiceStats] = useState<ServiceStat[]>([])
  const [loadingServiceStats, setLoadingServiceStats] = useState(false)
  const [showApiKeysModal, setShowApiKeysModal] = useState(false)
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loadingApiKeys, setLoadingApiKeys] = useState(false)
  const [newApiKey, setNewApiKey] = useState<string | null>(null)
  const [filters, setFilters] = useState<UserFilters>({
    status: '',
    balanceMin: '',
    balanceMax: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  })

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.status) params.append('status', filters.status)
      if (filters.balanceMin) params.append('balance_min', filters.balanceMin)
      if (filters.balanceMax) params.append('balance_max', filters.balanceMax)
      if (filters.dateFrom) params.append('date_from', filters.dateFrom)
      if (filters.dateTo) params.append('date_to', filters.dateTo)
      if (filters.search) params.append('search', filters.search)

      const result = await apiCall(`/api/admin/users?${params.toString()}`)
      setUsers(result.data ?? [])
    } catch (error) {
      console.error('Failed to fetch users', error)
      alert(error instanceof Error ? error.message : '获取用户列表失败')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void fetchUsers()
  }, [fetchUsers])

  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active'
    const action = newStatus === 'disabled' ? '禁用' : '启用'

    if (!confirm(`确定要${action}用户 ${user.email} 吗？`)) return

    try {
      await apiCall('/api/admin/users/status', {
        method: 'PUT',
        body: JSON.stringify({
          user_id: user.id,
          status: newStatus,
        }),
      })

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, status: newStatus } : u))
      )
      alert(`${action}成功`)
    } catch (error) {
      console.error('Failed to toggle user status', error)
      alert(error instanceof Error ? error.message : `${action}用户失败`)
    }
  }

  const handleAddPoints = async () => {
    if (!selectedUser) return

    const points = parseFloat(pointsToAdd)
    if (isNaN(points) || points <= 0) {
      alert('请输入有效的积分数量')
      return
    }

    try {
      await apiCall('/api/admin/users/points', {
        method: 'POST',
        body: JSON.stringify({
          user_id: selectedUser.id,
          points,
          note: pointsNote || '管理员充值',
        }),
      })

      setUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id ? { ...u, balance: u.balance + points } : u
        )
      )

      setShowAddPointsModal(false)
      setPointsToAdd('')
      setPointsNote('')
      alert('充值成功')
    } catch (error) {
      console.error('Failed to add points', error)
      alert(error instanceof Error ? error.message : '充值失败')
    }
  }

  const fetchUserTransactions = async (userId: string) => {
    setLoadingTransactions(true)
    try {
      const result = await apiCall(`/api/admin/users/${userId}/transactions`)
      setTransactions(result.data ?? [])
    } catch (error) {
      console.error('Failed to fetch transactions', error)
      alert(error instanceof Error ? error.message : '获取交易记录失败')
    } finally {
      setLoadingTransactions(false)
    }
  }

  const fetchUserServiceStats = async (userId: string) => {
    setLoadingServiceStats(true)
    try {
      const result = await apiCall(`/api/admin/users/${userId}/service-stats`)
      setServiceStats(result.data ?? [])
    } catch (error) {
      console.error('Failed to fetch service stats', error)
      alert(error instanceof Error ? error.message : '获取服务调用统计失败')
    } finally {
      setLoadingServiceStats(false)
    }
  }

  const handleViewDetails = (user: User) => {
    setSelectedUser(user)
    setShowDetailModal(true)
    void fetchUserTransactions(user.id)
    void fetchUserServiceStats(user.id)
  }

  const handleOpenAddPoints = (user: User) => {
    setSelectedUser(user)
    setShowAddPointsModal(true)
  }

  const fetchApiKeys = async (userId: string) => {
    setLoadingApiKeys(true)
    try {
      const result = await apiCall(`/api/admin/users/${userId}/api-keys`)
      setApiKeys(result.data ?? [])
    } catch (error) {
      console.error('Failed to fetch API keys', error)
      alert(error instanceof Error ? error.message : '获取API Key失败')
    } finally {
      setLoadingApiKeys(false)
    }
  }

  const handleOpenApiKeys = (user: User) => {
    setSelectedUser(user)
    setShowApiKeysModal(true)
    setNewApiKey(null)
    void fetchApiKeys(user.id)
  }

  const handleCreateApiKey = async () => {
    if (!selectedUser) return

    try {
      const result = await apiCall(`/api/admin/users/${selectedUser.id}/api-keys`, {
        method: 'POST',
        body: JSON.stringify({
          name: '管理员创建',
          note: '通过管理后台创建',
        }),
      })

      if (result.data) {
        setNewApiKey(result.data.api_key)
        setApiKeys((prev) => [result.data, ...prev])
      }
    } catch (error) {
      console.error('Failed to create API key', error)
      alert(error instanceof Error ? error.message : '创建API Key失败')
    }
  }

  const handleToggleApiKeyStatus = async (keyId: string, currentStatus: string) => {
    if (!selectedUser) return

    const newStatus = currentStatus === 'active' ? 'disabled' : 'active'

    try {
      await apiCall(`/api/admin/users/${selectedUser.id}/api-keys`, {
        method: 'PUT',
        body: JSON.stringify({
          key_id: keyId,
          status: newStatus,
        }),
      })

      setApiKeys((prev) =>
        prev.map((key) => (key.id === keyId ? { ...key, status: newStatus } : key))
      )
    } catch (error) {
      console.error('Failed to toggle API key status', error)
      alert(error instanceof Error ? error.message : '更新API Key状态失败')
    }
  }

  const handleDeleteApiKey = async (keyId: string) => {
    if (!selectedUser) return
    if (!confirm('确定要删除这个API Key吗？')) return

    try {
      await apiCall(
        `/api/admin/users/${selectedUser.id}/api-keys?key_id=${keyId}`,
        {
          method: 'DELETE',
        }
      )

      setApiKeys((prev) => prev.filter((key) => key.id !== keyId))
      alert('删除成功')
    } catch (error) {
      console.error('Failed to delete API key', error)
      alert(error instanceof Error ? error.message : '删除API Key失败')
    }
  }

  const handleFilterChange = (key: keyof UserFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  if (loading) {
    return (
      <section className="flex h-full w-full items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </section>
    )
  }

  return (
    <section className="h-full w-full p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">用户管理</h2>
        <p className="mt-1 text-sm text-gray-600">
          管理系统用户、余额充值、消费记录和API密钥
        </p>
      </div>

      {/* 筛选栏 */}
      <div className="mb-4 grid gap-4 rounded-lg bg-white p-4 shadow md:grid-cols-6">
        <input
          type="text"
          placeholder="搜索邮箱或姓名"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />

        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">所有状态</option>
          <option value="active">活跃</option>
          <option value="disabled">已禁用</option>
        </select>

        <input
          type="number"
          placeholder="最低余额"
          value={filters.balanceMin}
          onChange={(e) => handleFilterChange('balanceMin', e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />

        <input
          type="number"
          placeholder="最高余额"
          value={filters.balanceMax}
          onChange={(e) => handleFilterChange('balanceMax', e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />

        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />

        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {/* 用户列表 */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                用户
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                邮箱
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                余额
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                状态
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                注册时间
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm">
                  <div className="font-medium text-gray-900">
                    {user.name || '未设置'}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">{user.email}</td>
                <td className="px-6 py-4 text-sm">
                  <span className="font-semibold text-gray-900">
                    {user.balance.toFixed(2)}
                  </span>
                  <span className="ml-1 text-gray-500">积分</span>
                </td>
                <td className="px-6 py-4 text-sm">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      user.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {user.status === 'active' ? '活跃' : '已禁用'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium">
                  <button
                    onClick={() => handleViewDetails(user)}
                    className="mr-3 text-blue-600 hover:text-blue-800"
                  >
                    详情
                  </button>
                  <button
                    onClick={() => handleOpenAddPoints(user)}
                    className="mr-3 text-green-600 hover:text-green-800"
                  >
                    充值
                  </button>
                  <button
                    onClick={() => handleOpenApiKeys(user)}
                    className="mr-3 text-purple-600 hover:text-purple-800"
                  >
                    API Key
                  </button>
                  <button
                    onClick={() => handleToggleStatus(user)}
                    className={`${
                      user.status === 'active'
                        ? 'text-red-600 hover:text-red-800'
                        : 'text-green-600 hover:text-green-800'
                    }`}
                  >
                    {user.status === 'active' ? '禁用' : '启用'}
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-sm text-gray-500"
                >
                  没有找到用户
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 充值弹窗 */}
      {showAddPointsModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-6">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                充值积分 - {selectedUser.email}
              </h3>
              <button
                onClick={() => setShowAddPointsModal(false)}
                className="text-gray-400 transition hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 px-6 py-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  当前余额
                </label>
                <div className="mt-1 text-2xl font-bold text-gray-900">
                  {selectedUser.balance.toFixed(2)} 积分
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  充值金额
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pointsToAdd}
                    onChange={(e) => setPointsToAdd(e.target.value)}
                    placeholder="输入充值积分数量"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  备注（可选）
                  <input
                    type="text"
                    value={pointsNote}
                    onChange={(e) => setPointsNote(e.target.value)}
                    placeholder="充值原因或说明"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowAddPointsModal(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={handleAddPoints}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
                >
                  确认充值
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Key管理弹窗 */}
      {showApiKeysModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-6">
          <div className="w-full max-w-4xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                API Key 管理 - {selectedUser.email}
              </h3>
              <button
                onClick={() => setShowApiKeysModal(false)}
                className="text-gray-400 transition hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-6">
              {/* 新创建的Key提示 */}
              {newApiKey && (
                <div className="mb-4 rounded-lg bg-yellow-50 border border-yellow-200 p-4">
                  <p className="mb-2 font-semibold text-yellow-900">
                    新的API Key已创建！请复制保存，关闭后将无法再次查看：
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-sm">
                      {newApiKey}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(newApiKey)
                        alert('已复制到剪贴板')
                      }}
                      className="rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-700"
                    >
                      复制
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  管理用户的API密钥，可以创建、禁用或删除
                </p>
                <button
                  onClick={handleCreateApiKey}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
                >
                  创建新Key
                </button>
              </div>

              {loadingApiKeys ? (
                <div className="flex h-32 items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                          密钥
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                          名称
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                          状态
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                          创建时间
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {apiKeys.map((key) => (
                        <tr key={key.id}>
                          <td className="px-4 py-3 text-sm">
                            <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                              {key.key_hash.substring(0, 20)}...
                            </code>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {key.name}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                key.status === 'active'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {key.status === 'active' ? '活跃' : '已禁用'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {new Date(key.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium">
                            <button
                              onClick={() => handleToggleApiKeyStatus(key.id, key.status)}
                              className="mr-3 text-blue-600 hover:text-blue-800"
                            >
                              {key.status === 'active' ? '禁用' : '启用'}
                            </button>
                            <button
                              onClick={() => handleDeleteApiKey(key.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              删除
                            </button>
                          </td>
                        </tr>
                      ))}
                      {apiKeys.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-sm text-gray-500"
                          >
                            该用户还没有API Key
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 详情弹窗 */}
      {showDetailModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-6">
          <div className="w-full max-w-6xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                用户详情 - {selectedUser.email}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 transition hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-6">
              {/* 用户基本信息 */}
              <div className="mb-6 grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 md:grid-cols-4">
                <div>
                  <p className="text-sm text-gray-500">用户名</p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {selectedUser.name || '未设置'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">当前余额</p>
                  <p className="mt-1 text-xl font-bold text-blue-600">
                    {selectedUser.balance.toFixed(2)} 积分
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">状态</p>
                  <p className="mt-1">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        selectedUser.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {selectedUser.status === 'active' ? '活跃' : '已禁用'}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">注册时间</p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {new Date(selectedUser.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* 服务调用统计 */}
              <div className="mb-6">
                <h4 className="mb-4 text-base font-semibold text-gray-900">
                  服务调用统计
                </h4>
                {loadingServiceStats ? (
                  <div className="flex h-32 items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {serviceStats.map((stat) => (
                      <div
                        key={stat.service_id}
                        className="rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <h5 className="mb-2 font-semibold text-gray-900">
                          {stat.service_name}
                        </h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">总调用</span>
                            <span className="font-semibold text-gray-900">
                              {stat.total_calls} 次
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">成功率</span>
                            <span className="font-semibold text-green-600">
                              {stat.total_calls > 0
                                ? Math.round(
                                    (stat.successful_calls / stat.total_calls) * 100
                                  )
                                : 0}
                              %
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">消费积分</span>
                            <span className="font-semibold text-blue-600">
                              {stat.total_points.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">平均响应</span>
                            <span className="font-semibold text-gray-900">
                              {stat.avg_response_time}ms
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">最后调用</span>
                            <span className="text-xs text-gray-500">
                              {new Date(stat.last_call_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {serviceStats.length === 0 && (
                      <div className="col-span-3 rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
                        <p className="text-gray-500">暂无服务调用记录</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 交易记录 */}
              <div>
                <h4 className="mb-4 text-base font-semibold text-gray-900">
                  交易记录
                </h4>
                {loadingTransactions ? (
                  <div className="flex h-32 items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            时间
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            类型
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            积分变化
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            余额
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                            说明
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {transactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {new Date(transaction.created_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                  transaction.transaction_type === 'recharge'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {transaction.transaction_type === 'recharge'
                                  ? '充值'
                                  : '消费'}
                              </span>
                            </td>
                            <td
                              className={`px-4 py-3 text-sm font-semibold ${
                                transaction.transaction_type === 'recharge'
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {transaction.transaction_type === 'recharge'
                                ? '+'
                                : '-'}
                              {Math.abs(transaction.points).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {transaction.balance_after.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {transaction.service_name ||
                                transaction.description ||
                                '-'}
                            </td>
                          </tr>
                        ))}
                        {transactions.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-4 py-8 text-center text-sm text-gray-500"
                            >
                              暂无交易记录
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
